import React from 'react';
import { ExternalLink, Music } from 'lucide-react';

// Alle Utility-Funktionen direkt hier
const detectMusicLinkType = (url) => {
  if (!url || typeof url !== 'string') return null;
  const urlLower = url.toLowerCase().trim();
  if (urlLower.includes('spotify.com')) return 'spotify';
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return 'youtube';
  if (urlLower.includes('music.apple.com')) return 'apple';
  if (urlLower.startsWith('http://') || urlLower.startsWith('https://')) return 'link';
  return null;
};

const getSpotifyEmbedUrl = (url) => {
  try {
    const match = url.match(/spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/);
    if (match) {
      const [, type, id] = match;
      return `https://open.spotify.com/embed/${type}/${id}`;
    }
  } catch (e) {
    console.error('Spotify URL parse error:', e);
  }
  return null;
};

const getYouTubeEmbedUrl = (url) => {
  try {
    let videoId = null;
    if (url.includes('youtube.com/watch')) {
      const match = url.match(/[?&]v=([^&]+)/);
      if (match) videoId = match[1];
    } else if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^?]+)/);
      if (match) videoId = match[1];
    }
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  } catch (e) {
    console.error('YouTube URL parse error:', e);
  }
  return null;
};

export default function MusicPlayer({ url, title = "Lieblingssong / Podcast" }) {
  if (!url) return null;
  
  const linkType = detectMusicLinkType(url);
  
  if (!linkType) {
    return (
      <div>
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Music className="w-4 h-4 text-rose-500" />
          {title}
        </h4>
        <p className="text-gray-700">{url}</p>
      </div>
    );
  }
  
  if (linkType === 'spotify') {
    const embedUrl = getSpotifyEmbedUrl(url);
    if (embedUrl) {
      return (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Music className="w-4 h-4 text-rose-500" />
            {title}
          </h4>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe
              src={embedUrl}
              width="100%"
              height="152"
              frameBorder="0"
              allowTransparency="true"
              allow="encrypted-media"
              title="Spotify Player"
              className="w-full"
            />
          </div>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-rose-500 mt-2"
          >
            In Spotify öffnen <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      );
    }
  }
  
  if (linkType === 'youtube') {
    const embedUrl = getYouTubeEmbedUrl(url);
    if (embedUrl) {
      return (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Music className="w-4 h-4 text-rose-500" />
            {title}
          </h4>
          <div className="rounded-xl overflow-hidden shadow-lg aspect-video">
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube Player"
              className="w-full h-full"
            />
          </div>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-rose-500 mt-2"
          >
            Auf YouTube öffnen <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      );
    }
  }
  
  return (
    <div>
      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
        <Music className="w-4 h-4 text-rose-500" />
        {title}
      </h4>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
      >
        <Music className="w-4 h-4" />
        Jetzt anhören
        <ExternalLink className="w-4 h-4" />
      </a>
      <p className="text-xs text-gray-500 mt-2 break-all">{url}</p>
    </div>
  );
}